import React, { useState } from "react";
import api, { getAuthToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, FileImage, FileAudio, FileText, FileVideo } from "lucide-react";
import { toast } from "sonner";

export default function EvidenceUploader({ value = [], onChange, accept = "image/*,audio/*", label = "Attach photos or audio", testid = "evidence" }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onChange([...(value || []), data]);
      }
      toast.success("Uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = (i) => {
    const next = [...value];
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-border cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors text-sm" data-testid={`${testid}-upload-label`}>
        <Upload className="w-4 h-4" />
        <span>{uploading ? "Uploading..." : label}</span>
        <input
          type="file"
          accept={accept}
          multiple
          onChange={handleUpload}
          disabled={uploading}
          className="hidden"
          data-testid={`${testid}-upload-input`}
        />
      </label>
      {value?.length > 0 && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {value.map((f, i) => {
            const Icon = f.kind === "audio" ? FileAudio : f.kind === "document" ? FileText : FileImage;
            return (
              <li key={i} className="flex items-center gap-3 px-3 py-2 rounded-md border border-border bg-white text-sm" data-testid={`${testid}-item-${i}`}>
                <Icon className="w-4 h-4 text-slate-500 shrink-0" />
                <span className="truncate flex-1">{f.filename}</span>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-muted-foreground hover:text-destructive"
                  data-testid={`${testid}-remove-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function EvidenceViewer({ evidence = [] }) {
  const token = getAuthToken();
  if (!evidence?.length) return <p className="text-sm text-muted-foreground">No evidence attached.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="evidence-viewer">
      {evidence.map((f, i) => {
        const url = f.id ? `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${f.id}/download?auth=${token}` : null;
        if (!url) return null;
        if (f.kind === "audio") {
          return (
            <div key={i} className="p-3 rounded-md border bg-slate-50">
              <div className="text-xs text-muted-foreground mb-2 truncate">{f.filename}</div>
              <audio controls src={url} className="w-full" data-testid={`audio-${i}`} />
            </div>
          );
        }
        if (f.kind === "video") {
          return (
            <div key={i} className="p-3 rounded-md border bg-slate-50">
              <div className="text-xs text-muted-foreground mb-2 truncate">{f.filename}</div>
              <video controls src={url} className="w-full rounded" data-testid={`video-${i}`} />
            </div>
          );
        }
        if (f.kind === "document") {
          return (
            <a key={i} href={url} target="_blank" rel="noreferrer" className="p-3 rounded-md border bg-slate-50 flex items-center gap-3 hover:border-slate-400 transition-colors" data-testid={`doc-${i}`}>
              <FileText className="w-8 h-8 text-slate-500 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{f.filename}</div>
                <div className="text-xs text-muted-foreground">PDF · Click to open</div>
              </div>
            </a>
          );
        }
        return (
          <a key={i} href={url} target="_blank" rel="noreferrer" className="block group">
            <div className="rounded-md border bg-slate-50 overflow-hidden aspect-video">
              <img src={url} alt={f.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" data-testid={`image-${i}`} />
            </div>
            <div className="text-xs text-muted-foreground mt-1 truncate">{f.filename}</div>
          </a>
        );
      })}
    </div>
  );
}
